import {
  LoginDTO,
  LoginResponse,
  LoginResponseDTO,
  TwoFactorAuthDTO,
  UpdateUserDTO,
} from "@/lib/auth/auth-types";
import api from "@/lib/axiosConfig.ts";
import { getActiveRegion, getRegion, REGIONS } from "@/config/regions";
import { PENDING_2FA_REGION_KEY } from "@/context/auth/AuthProvider";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { HttpStatusCode } from "axios";
import axios from "axios";

interface ResetPasswordResponse {
  success?: boolean;
  token?: string;
}

async function syncPasswordResetTokenToRegion(
  baseUrl: string,
  email: string,
  token: string,
): Promise<void> {
  try {
    await axios.post(
      `${baseUrl}auth/reset?email=${encodeURIComponent(email)}&token=${encodeURIComponent(token)}&syncOnly=true`,
    );
  } catch (err) {
    console.warn("[auth] password reset token sync failed", baseUrl, err);
  }
}

async function resetPassword(email: string, urlWebsite: string) {
  let resetToken: string | null = null;

  for (const region of REGIONS) {
    try {
      const { data } = await axios.post<ResetPasswordResponse>(
        `${region.baseUrl}auth/reset?email=${encodeURIComponent(email)}&urlWebsite=${encodeURIComponent(urlWebsite)}`,
      );
      if (data?.token) {
        resetToken = data.token;
        break;
      }
    } catch (err) {
      console.warn("[auth] password reset failed on region", region.code, err);
    }
  }

  if (!resetToken) {
    throw new Error("no_user_account");
  }

  await Promise.all(
    REGIONS.map((region) =>
      syncPasswordResetTokenToRegion(region.baseUrl, email, resetToken!),
    ),
  );

  return { success: true, token: resetToken };
}

async function redeemPasswordOnRegion(
  baseUrl: string,
  redeemPasswordData: RedeemPasswordDTO,
): Promise<boolean> {
  try {
    const { data } = await axios.post<boolean>(
      `${baseUrl}back/api/user/redeem`,
      redeemPasswordData,
    );
    return data === true;
  } catch (err) {
    console.warn("[auth] password redeem failed on region", baseUrl, err);
    return false;
  }
}

async function redeemPassword(redeemPasswordData: RedeemPasswordDTO) {
  const results = await Promise.all(
    REGIONS.map((region) =>
      redeemPasswordOnRegion(region.baseUrl, redeemPasswordData),
    ),
  );

  const success = results.some(Boolean);
  if (!success) {
    throw new Error("password_redeem_failed");
  }

  return true;
}

export async function login(authInfos: LoginDTO): Promise<LoginResponse> {
  try {
    const { data, status } = await api.post<LoginResponseDTO>(
      "/auth/signin?locale=" + authInfos.locale,
      {
        email: authInfos.email,
        password: authInfos.password,
      },
      {
        withCredentials: true,
      },
    );
    return {
      ...data,
      status,
    };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status ?? HttpStatusCode.BadRequest;
      return {
        status: status,
        message:
          error.response?.data?.message ?? "Unknown error. Check server logs.",
      };
    }
    throw error;
  }
}

export async function twoFactorAuth(
  authInfos: TwoFactorAuthDTO,
): Promise<LoginResponse> {
  try {
    const { data, status } = await api.post<LoginResponseDTO>(
      "/auth/2fa?locale=" + authInfos.locale,
      {
        email: authInfos.email,
        twoFactorToken: authInfos.twoFactorToken,
      },
      {
        withCredentials: true,
      },
    );
    return {
      ...data,
      status,
    };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status ?? HttpStatusCode.BadRequest;
      return {
        status: status,
        message:
          error.response?.data?.message ?? "Unknown error. Check server logs.",
      };
    }
    throw error;
  }
}

export async function resend2FA(email: string): Promise<{ status: number }> {
  const pendingCode = sessionStorage.getItem(PENDING_2FA_REGION_KEY);
  const baseUrl =
    getRegion(pendingCode)?.baseUrl ?? getActiveRegion().baseUrl;
  const { status } = await axios.post<LoginResponseDTO>(
    `${baseUrl}auth/2fa/resend`,
    { email },
    {
      withCredentials: true,
      validateStatus: (code) =>
        (code >= 200 && code < 300) || code === HttpStatusCode.Gone,
    },
  );
  return { status };
}

async function updateUser({
  userId,
  dto,
}: {
  userId: string;
  dto: UpdateUserDTO;
}) {
  const { data } = await api.patch("/back/api/user/" + userId, dto);
  return data;
}

export const useUpdateUser = (token: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateUser,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["user", token] }),
  });
};

async function getUser(token: string | null) {
  if (token) {
    const { data } = await api.get("/back/api/user/" + token);
    return data;
  }
}

export const useUser = (token: string | null) => {
  return useQuery({
    queryKey: ["user", token],
    queryFn: () => getUser(token),
    placeholderData: keepPreviousData,
    enabled: !!token,
  });
};

export const useResetPassword = (email: string, urlWebsite: string) => {
  return useMutation({
    mutationFn: () => resetPassword(email, urlWebsite),
  });
};

interface RedeemPasswordDTO {
  user: {
    email: string;
    password: string;
    confirm_password: string;
  };
  token: string;
}

export const useRedeemPassword = () => {
  return useMutation({
    mutationFn: (redeemPasswordData: RedeemPasswordDTO) =>
      redeemPassword(redeemPasswordData),
  });
};
